import React from 'react'
import { XStack, YStack } from 'tamagui'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Overview = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <DocSection id='what-is-a-guild' title={t`What is a Guild?`}>
          <Paragraph size='$5'>
            <Trans>
              A Guild is your community&apos;s home on Guild — the central hub
              where your <Link href='/docs/events/overview'>Events</Link>,{' '}
              <Link href='/docs/presentations/overview'>Presentations</Link>,
              and <Link href='/docs/guilds/members'>Members</Link> all come
              together. Whether you&apos;re running a local tech meetup, a hobby
              group, a professional network, or a developer community, a Guild
              gives you a dedicated space to organize and grow.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              Every Guild has its own profile page with a unique URL that you
              can share with anyone. Visitors can see what your community is
              about, browse upcoming Events, explore recorded Presentations, and
              meet the Members who make it all happen.
            </Trans>
          </Paragraph>
          <DocScreenshot
            src='docs/guilds/guild-profile-page.png'
            alt={t`Toronto JavaScript guild profile page showing cover photo, profile photo, description, member count, upcoming events, and tab navigation`}
            caption={t`A Guild profile page — the home for your community`}
          />
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection
          id='what-guilds-include'
          title={t`What Every Guild Includes`}
        >
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='home'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Profile page
                  </Paragraph>{' '}
                  — A customizable page with your Guild&apos;s cover photo,
                  profile photo, name, description, and Member count. This is
                  the first thing visitors see and the main entry point for your
                  community. See{' '}
                  <Link href='/docs/guilds/profile'>Guild Profile Page</Link>{' '}
                  for details.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='calendar'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Events
                  </Paragraph>{' '}
                  — Create and manage in-person or online Events with RSVPs,
                  waitlists, ticketing, and venue details. Your Guild&apos;s
                  Events tab gives visitors a full view of what&apos;s coming up
                  and what&apos;s already happened. See{' '}
                  <Link href='/docs/events/overview'>Events</Link> to learn
                  more.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='projection-screen'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Presentations
                  </Paragraph>{' '}
                  — Share recorded talks, slide decks, and session content from
                  your Events. Your Guild&apos;s Presentations tab creates a
                  permanent library of your community&apos;s knowledge. See{' '}
                  <Link href='/docs/presentations/overview'>Presentations</Link>{' '}
                  to learn more.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='people'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Members
                  </Paragraph>{' '}
                  — Manage your community with roles and permissions. Organizers
                  run the Guild, Editors help manage content, and Members
                  participate in Events. See{' '}
                  <Link href='/docs/guilds/members'>Managing Members</Link> for
                  the full breakdown.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon
                name='link'
                size='$5'
                color='$color'
                flexShrink={0}
                marginTop={2}
              />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    Integrations
                  </Paragraph>{' '}
                  — Connect your Guild to{' '}
                  <Link href='/docs/guilds/bluesky'>Bluesky</Link> for automatic
                  Event announcements,{' '}
                  <Link href='/docs/guilds/google-calendar'>
                    Google Calendar
                  </Link>{' '}
                  for syncing Events, and{' '}
                  <Link href='/docs/guilds/calendar-feeds'>Calendar Feeds</Link>{' '}
                  for subscribing from any calendar app.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection
          id='public-vs-approval'
          title={t`Public and Approval-Based Guilds`}
        >
          <Paragraph size='$5'>
            <Trans>
              By default, Guilds are public — anyone can join with a single
              click on the Join button. This is ideal for open communities that
              want to grow quickly and welcome everyone.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              For communities that need more control over who joins, Organizers
              can set up a{' '}
              <Link href='/docs/guilds/forms'>Membership Form</Link> that
              prospective Members fill out when requesting to join. Organizers
              can then review applications and approve or deny each one
              individually. This is useful for professional networks,
              invite-only groups, or communities that want to collect
              information about their Members upfront.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='getting-started' title={t`Ready to Get Started?`}>
          <Paragraph size='$5'>
            <Trans>
              Creating a Guild takes just a few minutes. Head over to{' '}
              <Link href='/docs/guilds/creation'>Creating a Guild</Link> for a
              step-by-step guide, or explore the{' '}
              <Link href='/docs/guilds/settings'>Guild Settings</Link> page to
              see everything you can customize.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Overview
